import escapeStringRegexp from 'escape-string-regexp'
import {useEffect, useMemo, useCallback} from 'react'

import {useQueryDependenciesSearch} from './useQueryDependenciesSearch'

import {useDebounceValue} from 'src/hooks/useDebounceValue'
import {restApi} from 'src/services/rest'
import type {BuildId} from 'src/types'

interface Props {
  search: string
  buildId: BuildId
  showFromDependencies: boolean
  skip: boolean
}

const SEARCH_DEBOUNCE = 500 //ms

export function useQueryDependenciesParameters(props: Props) {
  const {buildId, search, showFromDependencies, skip} = props

  const [fetchAllParameters, allParameters] = restApi.endpoints.getBuildProperties.useLazyQuery({
    selectFromResult: ({data, isLoading, isFetching}) => ({
      resultingProperties: data?.resultingProperties,
      isLoaded: data?.resultingProperties?.property != null,
      isLoading: isLoading || isFetching,
    }),
  })

  const [fetchSearch, searchParameters] = useQueryDependenciesSearch()

  const debouncedSearch = useDebounceValue(search, SEARCH_DEBOUNCE)
  const normalizedSearch = useMemo(
    () => escapeStringRegexp(debouncedSearch.toLowerCase().trim()),
    [debouncedSearch],
  )
  const loadAllParameters = ['', 'd', 'de', 'dep', 'dep\\.'].includes(normalizedSearch)

  const fetch = useCallback(() => {
    if (skip) {
      return () => undefined
    }

    const fields = showFromDependencies ? 'property(name,value)' : 'count'

    if (loadAllParameters || allParameters.isLoaded) {
      const result = fetchAllParameters(
        {
          buildLocator: `id:${buildId}`,
          fields: `resultingProperties(${fields},$locator:(name:(value:(^dep\\..*),matchType:matches,ignoreCase:true)))`,
        },
        true,
      )

      return () => {
        result.abort()
      }
    } else {
      return fetchSearch({
        buildId,
        normalizedSearch,
        fields,
      })
    }
  }, [
    allParameters.isLoaded,
    buildId,
    fetchAllParameters,
    fetchSearch,
    loadAllParameters,
    normalizedSearch,
    showFromDependencies,
    skip,
  ])

  useEffect(() => {
    if (allParameters.isLoaded) {
      return () => undefined
    }

    return fetch()
  }, [allParameters.isLoaded, fetch])

  const state = useMemo(() => {
    if (loadAllParameters || allParameters.isLoaded) {
      return {
        resultingProperties: allParameters.resultingProperties,
        startProperties: Object.fromEntries(
          allParameters.resultingProperties?.property?.map(
            property => [property.name, property.value] as [string, string],
          ) ?? [],
        ),
        isLoading: allParameters.isLoading,
        isClientSearch: allParameters.isLoaded,
      }
    }

    return {...searchParameters, isClientSearch: false}
  }, [
    allParameters.isLoaded,
    allParameters.isLoading,
    allParameters.resultingProperties,
    loadAllParameters,
    searchParameters,
  ])

  return {
    fetch,
    state,
  }
}
